import { useCallback, useMemo } from 'react';
import { dashboardStore } from 'src/pages/dashboards/useDashboardStore';

import { sanitiseSettings } from '../pages/dashboards/universal-widget/sanitiseSettings';
import type { GigawidgetSettings } from '../pages/dashboards/universal-widget/util';
import { useWidgetContext } from '../pages/dashboards/widget-context/WidgetContext';

export const useDashboardWidgetSettings = <T = unknown,>(): [
  null | T,
  (settings: T) => void,
] => {
  // Note, widgetId can be undefined in preview mode as the widget is not yet on
  // the dashboard.
  const widgetId = useWidgetContext()?.widgetId;

  // A per-widget selector rather than a plain `useDashboardWidgets()` slice:
  // every widget on the board mounts this hook, so subscribing to the whole
  // array would keep the drag cascade alive. zustand compares only the selector
  // RESULT, and `processWidgets` preserves each widget's `settings` reference
  // when it spreads — so moving widget A does not re-render widget B's settings.
  const storedSettings = dashboardStore((s) =>
    widgetId
      ? (
          s.widgets.find((w) => w.id === widgetId) ??
          s.myItemsWidgets.find((w) => w.id === widgetId)
        )?.settings
      : undefined
  );

  const settings = useMemo(
    () =>
      storedSettings
        ? sanitiseSettings(storedSettings as GigawidgetSettings)
        : undefined,
    [storedSettings]
  );

  // Reads through `getState()` instead of subscribing so the deps collapse to
  // `[widgetId]` and the callback identity is stable — ENG-560's memos take this
  // as an input. Writes a new array rather than mutating a store-owned widget:
  // the in-place mutation this replaces only worked because `processWidgets`
  // copied on write from a read path, and it now runs on write only.
  const setWidgetSettings = useCallback(
    (settings: T) => {
      if (!widgetId) {
        return;
      }
      const { widgets, myItemsWidgets, actions } = dashboardStore.getState();

      if (widgets.some((w) => w.id === widgetId)) {
        actions.setWidgets(
          widgets.map((w) => (w.id === widgetId ? { ...w, settings } : w))
        );

        return;
      }

      if (myItemsWidgets.some((w) => w.id === widgetId)) {
        actions.setMyItemsWidgets(
          myItemsWidgets.map((w) =>
            w.id === widgetId ? { ...w, settings } : w
          )
        );
      }
    },
    [widgetId]
  );

  return [settings as unknown as T, setWidgetSettings];
};
